import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Home } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';

// JWT payloads are base64url-encoded (RFC 7515): replace URL-safe chars and
// re-pad before atob() to avoid InvalidCharacterError on real-world tokens.
function decodeBase64Url(input: string): string {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = normalized.length % 4 === 0 ? 0 : 4 - (normalized.length % 4);
  const padded = normalized + '='.repeat(pad);
  const binary = atob(padded);
  // Decode UTF-8 safely (atob returns latin1).
  try {
    return decodeURIComponent(
      Array.from(binary)
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
  } catch {
    return binary;
  }
}

function getActiveTenantSlug(): string | null {
  // Prefer the slug stored alongside the active session token.
  const stored = localStorage.getItem(AUTH_STORAGE_KEYS.TENANT_SLUG);
  if (stored && stored.trim()) return stored.trim();

  // Fallback: decode the JWT to extract the tenant slug, in case the slug key
  // was cleared but the session token is still valid.
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = JSON.parse(decodeBase64Url(parts[1]!));
    const slug = payload.tenant_slug || payload.tenantSlug;
    return typeof slug === 'string' && slug.length > 0 ? slug : null;
  } catch {
    return null;
  }
}

export default function TenantNotFound() {
  const navigate = useNavigate();
  const activeSlug = getActiveTenantSlug();

  const handleGoHome = () => {
    // Preserve session: if the user is logged in to a real tenant, send them
    // to that tenant's dashboard via SPA navigation (no page reload, no logout).
    if (activeSlug) {
      navigate(`/${activeSlug}/dashboard`, { replace: true });
      return;
    }
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="mb-8">
          <AlertTriangle className="h-24 w-24 text-red-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <h2 className="text-2xl font-semibold text-gray-700 mb-4">Pagina non trovata</h2>
          <p className="text-gray-600 mb-8">
            La pagina richiesta non esiste o non è raggiungibile da questa area.
            La tua sessione è ancora attiva: torna alla dashboard per continuare a lavorare.
          </p>
        </div>

        <div className="space-y-4">
          <Button
            onClick={handleGoHome}
            className="w-full"
            size="lg"
          >
            <Home className="h-4 w-4 mr-2" />
            {activeSlug ? 'Vai alla dashboard' : 'Vai alla home'}
          </Button>

          <p className="text-sm text-gray-500">
            Se ritieni che si tratti di un errore, contatta l'amministratore di sistema.
          </p>
        </div>
      </div>
    </div>
  );
}
